import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, CreditCard, Package, Type } from "lucide-react";
import { useI18n } from "@/i18n";

const Callout = ({ className, label, value, testId }) => (
  <div className={`absolute hidden sm:flex items-center gap-2 ${className}`} data-testid={testId}>
    <span className="h-px w-10 bg-black/40" />
    <div className="rounded-md border border-black/15 bg-white px-2 py-1">
      <div className="rpm-label !text-[10px]">{label}</div>
      <div className="font-mono-rpm text-xs font-medium text-black">{value}</div>
    </div>
  </div>
);

export const Hero = () => {
  const { t } = useI18n();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, -40]);
  const marquee = t("hero.marquee");

  return (
    <section className="rpm-hero-wash rpm-noise relative overflow-hidden" data-testid="hero-section">
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-4 pb-16 pt-8 sm:px-6 lg:grid-cols-[1.1fr_0.9fr] lg:gap-6 lg:pb-24 lg:pt-16">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.45, ease: [0.2, 0.8, 0.2, 1] }}
          className="order-1"
        >
          <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-black/10 bg-white px-3 py-1.5">
            <span className="h-2 w-2 rounded-full bg-[var(--rpm-orange)]" />
            <span className="rpm-label !text-black">{t("hero.badge")}</span>
          </div>
          <h1 className="font-display text-[34px] font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-[56px]" data-testid="hero-title">
            {t("hero.title1")}
            <br />
            {t("hero.title2")}{" "}
            <span className="relative z-0 inline-block whitespace-nowrap">
              {t("hero.title3")}
              <span className="absolute -bottom-0.5 left-0 -z-10 h-3 w-full bg-[var(--rpm-lime)] sm:h-4" aria-hidden />
            </span>
          </h1>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-zinc-700 sm:text-lg">{t("hero.sub")}</p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <a href="#order" className="rpm-btn rpm-btn-orange h-14 px-7 text-base" data-testid="hero-build-order-button">
              {t("hero.cta")} <ArrowRight className="h-5 w-5" />
            </a>
            <a href="#products" className="rpm-btn rpm-btn-lime h-14 px-7 text-base" data-testid="hero-view-products-button">
              {t("hero.cta2")}
            </a>
          </div>
          <div className="mt-9 grid max-w-xl grid-cols-3 gap-3">
            {[
              { icon: Type, t: t("hero.f1"), s: t("hero.f1s") },
              { icon: CreditCard, t: t("hero.f2"), s: t("hero.f2s") },
              { icon: Package, t: t("hero.f3"), s: t("hero.f3s") },
            ].map(({ icon: I, t: title, s }) => (
              <div key={title} className="rounded-2xl border border-zinc-200 bg-white p-3 sm:p-4">
                <I className="mb-2 h-5 w-5" />
                <div className="font-display text-base font-semibold sm:text-lg">{title}</div>
                <div className="text-xs text-zinc-600">{s}</div>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div style={{ y }} className="relative order-2 mx-auto w-full max-w-[420px]">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
            className="rpm-grid-bg relative rounded-[28px] border border-zinc-200 p-8 sm:p-12"
          >
            <img src="/logo.png" alt="RPM 3D Print" className="mx-auto w-full max-w-[340px] mix-blend-multiply" data-testid="hero-logo-image" />
            <Callout className="-left-6 top-[18%]" label={t("hero.extruder")} value="Ø 0.4 mm" testId="hero-callout-extruder" />
            <Callout className="-right-8 top-[52%] flex-row-reverse" label={t("hero.layer")} value="0.2 mm" testId="hero-callout-layer" />
            <div className="absolute bottom-4 left-5 rpm-label">FIG. 01 — RPM</div>
            <div className="absolute right-5 top-4 rpm-label">LV / EU</div>
          </motion.div>
        </motion.div>
      </div>

      <div className="overflow-hidden border-y border-black bg-black py-3 text-white" aria-hidden>
        <div className="rpm-marquee">
          {Array.from({ length: 2 }).map((_, k) => (
            <div key={k} className="flex shrink-0 items-center">
              {marquee.map((item) => (
                <span key={item} className="mx-6 flex items-center gap-6 font-mono-rpm text-sm tracking-[0.18em]">
                  {item}
                  <span className="h-1.5 w-1.5 rounded-full bg-[var(--rpm-lime)]" />
                </span>
              ))}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
